import { AutocompleteChangeReason, AutocompleteProps } from '@mui/material';
import {
  FormEvent, useCallback, useEffect, useState,
} from 'react';
import { SearchFieldProps } from './types';

type UseSearchFieldReturn<Value> = [
  (event: FormEvent) => void,
  AutocompleteProps<Value, false, false, true>['onChange'],
  AutocompleteProps<Value, false, false, true>['onInputChange'],
];

interface UseSearchFieldProps<Value> {
  inputValue?: SearchFieldProps<Value>['inputValue'];
  value?: SearchFieldProps<Value>['value'];
  onChange?: SearchFieldProps<Value>['onChange'];
  onSelect?: SearchFieldProps<Value>['onSelect'];
  onSubmit: SearchFieldProps<Value>['onSubmit'];
}

export default function useSearchField<Value>(props: UseSearchFieldProps<Value>):
UseSearchFieldReturn<Value> {
  const {
    value, inputValue, onChange, onSelect, onSubmit,
  } = props;
  const [submitValue, setSubmitValue] = useState<unknown>(undefined);

  useEffect(
    () => {
      if (value !== undefined) {
        setSubmitValue(value);
      }
    },
    [value],
  );

  useEffect(
    () => {
      if (inputValue !== undefined) {
        setSubmitValue(submitValue);
      }
    },
    [inputValue, submitValue],
  );

  const iOnInputUpdate = useCallback(
    (inEvent: React.ChangeEvent<HTMLInputElement>, iValue: string) => {
      if (onChange && inEvent) {
        const event = { ...inEvent };
        if (event.target.value === undefined) {
          event.target.value = '';
        }
        onChange(event);
      }

      setSubmitValue(iValue);
    },
    [onChange],
  );

  const iOnChange = useCallback(
    (
      event: React.SyntheticEvent,
      iValue: SearchFieldProps<Value>['value'],
      reason: AutocompleteChangeReason,
    ) => {
      if (onSelect) {
        onSelect(event, iValue!, reason);
      }

      setSubmitValue(iValue);
    },
    [onSelect],
  );

  const iOnSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      onSubmit(submitValue as string | Value | null, event);
    },
    [onSubmit, submitValue],
  );

  return [iOnSubmit, iOnChange, iOnInputUpdate];
}
